import { ARTICLE_AUTHOR, ARTICLE_PUBLISHER, SPEAKABLE } from "@/lib/article-schema";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { KeyTakeaways } from "@/components/site/KeyTakeaways";
import { TRIP_BLOGS } from "@/data/trip-blogs";
import { COUNTRIES, roundFlagUrl, type CountrySlug } from "@/data/countries";
import { CalendarDays, Wallet, Clock, ArrowRight, ChevronLeft, MapPin } from "lucide-react";
import { makeSeoTitle } from "@/lib/seo-title";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { makeSeoDescription } from "@/lib/seo-description";

const VALID = new Set(COUNTRIES.map((c) => c.slug));

const TRIP_PUBLISHED = "2026-01-15";
const TRIP_UPDATED = "2026-07-01";

export const Route = createFileRoute("/trip/$country")({
  beforeLoad: ({ params }) => {
    if (!VALID.has(params.country as CountrySlug)) throw notFound();
  },
  head: ({ params }) => {
    const slug = params.country as CountrySlug;
    const country = COUNTRIES.find((c) => c.slug === slug);
    const blog = TRIP_BLOGS[slug];
    if (!country || !blog) return {};
    const url = `https://lifewestway.com/trip/${slug}`;
    const description = blog.intro.length > 160 ? blog.intro.slice(0, 157) + "…" : blog.intro;
    const seoTitle = makeSeoTitle(blog.headline);
    return {
      meta: [
        { title: seoTitle },
        { name: "description", content: makeSeoDescription(description) },
        { property: "og:title", content: seoTitle },
        { property: "og:description", content: makeSeoDescription(description) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...socialImageMeta({ image: blog.image, alt: `${country.name} travel guide 2026` }),
        { name: "twitter:title", content: seoTitle },
        { name: "twitter:description", content: makeSeoDescription(description) },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelGuide",
            headline: blog.headline,
            description,
            image: [blog.image],
            about: country.name,
            author: ARTICLE_AUTHOR,
            publisher: ARTICLE_PUBLISHER,
            speakable: SPEAKABLE,
            datePublished: TRIP_PUBLISHED,
            dateModified: TRIP_UPDATED,
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
      ],
    };
  },
  component: TripCountryPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-foreground">Trip guide not found</h1>
      <p className="mt-3 text-muted-foreground">
        We don&apos;t have a travel guide for that destination yet.
      </p>
      <Link title="Return to the LifeWestWay homepage" to="/" className="mt-6 inline-block text-accent underline">
        Back to home
      </Link>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-foreground">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      <button
        type="button"
        onClick={reset}
        className="mt-6 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white"
      >
        Try again
      </button>
    </div>
  ),
});

function TripCountryPage() {
  const { country: slug } = Route.useParams();
  const country = COUNTRIES.find((c) => c.slug === slug)!;
  const blog = TRIP_BLOGS[slug as CountrySlug];
  const subtitle = blog.intro.split(". ")[0] + ".";

  return (
    <article className="bg-card">
      {/* HERO */}
      <header className="relative isolate overflow-hidden">
        <div className="relative aspect-[21/9] w-full min-h-[380px] sm:min-h-[460px]">
          <SafeImage width={1200} height={675}
            src={blog.image}
            alt={`${country.name} — ${country.tagline}`}
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager" decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/10" />

          <div className="relative mx-auto flex h-full max-w-4xl flex-col justify-end px-4 pb-10 pt-24 sm:px-6 sm:pb-14">
            <Link title="Return to the LifeWestWay homepage"
              to="/"
              className="mb-6 inline-flex w-fit items-center gap-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80 transition-colors hover:text-accent"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back to Home
            </Link>

            <div className="flex items-center gap-3">
              <SafeImage width={64} height={64}
                src={roundFlagUrl(country.code)}
                alt={`${country.name} flag — plan your trip to ${country.name}`}
                className="h-10 w-10 rounded-full ring-2 ring-white shadow"
              />
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-card/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent backdrop-blur">
                <MapPin className="h-3 w-3" />
                Trip Guide · 2026
              </span>
            </div>

            {/* H1 — main title, contains keyword (country name) */}
            <h1 className="mt-4 font-display text-4xl font-black leading-[1.05] text-white sm:text-5xl lg:text-6xl">
              {blog.headline}
            </h1>

            {/* Subtitle / summary */}
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
              {subtitle}
            </p>
          </div>
        </div>
      </header>

      {/* META STRIP */}
      <div className="border-b border-border bg-gradient-to-b from-card to-secondary/40">
        <div className="mx-auto grid max-w-4xl grid-cols-1 divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <MetaCell
            icon={CalendarDays}
            label="Best Time"
            value={blog.bestTime}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
          />
          <MetaCell
            icon={Wallet}
            label="Daily Budget"
            value={blog.budget}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
          />
          <MetaCell
            icon={Clock}
            label="Ideal Duration"
            value={blog.duration}
            iconBg="bg-sky-100"
            iconColor="text-sky-600"
          />
        </div>
      </div>


      {/* BODY */}
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <p className="text-lg leading-relaxed text-foreground/85 sm:text-xl">{blog.intro}</p>

        <KeyTakeaways
          intro={blog.intro}
          sections={blog.sections.flatMap((s) => [
            { type: "h2", text: s.h2 },
            { type: "p", text: s.body },
          ])}
        />

        {blog.sections.map((section, i) => (
          <section key={i} className="mt-14">
            {/* H2 — main sections */}
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              <span className="mr-3 text-accent">{String(i + 1).padStart(2, "0")}</span>
              {section.h2}
            </h2>
            <div className="mt-2 h-px w-16 bg-accent" />
            <p className="mt-4 text-base leading-relaxed text-foreground/80 sm:text-[17px]">
              {section.body}
            </p>

            {section.subsections?.map((sub, j) => (
              <div key={j} className="mt-8 rounded-xl border-l-2 border-accent/50 bg-secondary/20 py-5 pl-5 pr-4">
                {/* H3 — supporting sub-points */}
                <h3 className="font-display text-lg font-semibold text-foreground sm:text-xl">
                  {sub.h3}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground/75">{sub.body}</p>

                {sub.points && sub.points.length > 0 && (
                  <dl className="mt-5 space-y-4">
                    {sub.points.map((pt, k) => (
                      <div key={k} className="border-t border-border/70 pt-3">
                        {/* H4 — specific details / FAQ items */}
                        <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-accent sm:text-sm">
                          {pt.h4}
                        </h4>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground/70">
                          {pt.body}
                        </p>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            ))}
          </section>
        ))}

        {/* CTA */}
        <div className="mt-16 rounded-2xl border border-border bg-navy p-6 text-navy-foreground sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
            Next Step
          </p>
          <h3 className="mt-2 font-display text-xl font-bold sm:text-2xl">
            Ready to make {country.name} home?
          </h3>
          <p className="mt-2 text-sm text-white/70 sm:text-base">
            Explore visa pathways, jobs, tax, and cost of living in our full country hub.
          </p>
          <Link title={`Open the full ${slug} country guide`}
            to="/$country"
            params={{ country: slug }}
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent/90"
          >
            Open {country.shortName} guide
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Other destinations */}
        <section className="mt-16">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
            Plan a trip to another destination
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {COUNTRIES.filter((c) => c.slug !== slug).map((c) => (
              <Link title={`Plan your trip to ${c.name}`}
                key={c.slug}
                to="/trip/$country"
                params={{ country: c.slug }}
                className="group flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 transition-colors hover:border-accent hover:bg-secondary/40"
              >
                <SafeImage width={64} height={64}
                  src={roundFlagUrl(c.code)}
                  alt={`${c.name} flag`}
                  className="h-7 w-7 rounded-full ring-1 ring-border"
                />
                <span className="text-sm font-semibold text-foreground group-hover:text-accent">
                  {c.shortName}
                </span>
              </Link>
            ))}
          </div>
        </section>

        <ArticleDisclaimer />
      </div>

      <ShareBar title={blog.headline} />
    </article>
  );
}

function MetaCell({
  icon: Icon,
  label,
  value,
  iconBg,
  iconColor,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: string;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="flex items-center gap-3 px-6 py-5">
      <span
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ring-1 ring-inset ring-black/5 shadow-sm ${iconBg}`}
      >
        <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={2.25} />
      </span>
      <div className="min-w-0">
        <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </p>
        <p className="mt-1 font-sans text-[15px] font-semibold leading-snug text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}

